import { Inject, Injectable, InjectionToken } from '@angular/core';
import * as LogRocket from 'logrocket';
import { environment } from '../../../environments/environment';
import { LogRocketUser, LogRocketEventType } from '../models/log-rocket.model';

/**
 * The LogRocket application ID. Empty in every environment except production, which is what
 * gates the whole integration off outside prod (CHAT-166). Injected rather than read straight
 * from `environment` purely so the gate can be driven from a unit test.
 *
 * Note: `environment.production` is NOT the discriminator — it is `true` in dv, qa and demo too.
 */
export const LOG_ROCKET_APP_ID = new InjectionToken<string>('LOG_ROCKET_APP_ID', {
  providedIn: 'root',
  factory: () => environment.logRocketAppId
});

/**
 * How long to wait for LogRocket to confirm the session is live before giving up and letting
 * the caller continue. `getSessionURL` never fires when ingest is unreachable — ad blockers and
 * the visual-QA determinism blocklist both do exactly that — so this timeout resolves rather
 * than rejects: a blocked LogRocket must never stall login (CHAT-299).
 */
export const LOG_ROCKET_SESSION_TIMEOUT_MS = 3000;

@Injectable({
  providedIn: 'root'
})
export class LogRocketService {
  private readyPromise: Promise<void> | null = null;

  constructor(@Inject(LOG_ROCKET_APP_ID) private readonly appId: string) { }

  private get enabled(): boolean {
    return !!this.appId && this.appId.trim().length > 0;
  }

  /**
   * Starts the LogRocket session and resolves once it is live (or the timeout elapses).
   * Safe to call repeatedly — `init` runs at most once. Resolves immediately when disabled.
   */
  initialize(): Promise<void> {
    if (!this.enabled) {
      return Promise.resolve();
    }

    if (!this.readyPromise) {
      // Assigned unconditionally: startSession() cannot throw or reject, so a vendor failure
      // still counts as "we tried once" rather than leaving init() to be retried on every call.
      this.readyPromise = this.startSession();
    }

    return this.readyPromise;
  }

  private startSession(): Promise<void> {
    // Both vendor calls sit inside the executor so that a synchronous throw from either becomes
    // a rejection of sessionReady rather than escaping initialize(). The .catch below then turns
    // that into a resolve: LogRocket failing to start must never stop auth-callback from
    // identifying the user and routing (CHAT-166 / CHAT-299).
    const sessionReady = new Promise<void>(resolve => {
      this.init(this.appId);
      this.onSessionUrl(() => resolve());
    });

    const timeout = new Promise<void>(resolve => {
      setTimeout(() => resolve(), LOG_ROCKET_SESSION_TIMEOUT_MS);
    });

    return Promise.race([sessionReady, timeout]).catch(error => {
      console.warn('LogRocket failed to start a session; continuing without one', error);
    });
  }

  identifyUser(user: LogRocketUser): void {
    if (!this.enabled) {
      return;
    }

    if (!user || !user.id) {
      return;
    }

    const userProperties: Record<string, any> = {
      name: user.name,
      email: user.email
    };

    if (user.companyName) {
      userProperties.companyName = user.companyName;
    }

    if (user.status) {
      userProperties.status = user.status;
    }

    if (user.privileges && user.privileges.length > 0) {
      userProperties.privileges = user.privileges.join(', ');
    }

    // Same reasoning as startSession(): whatever makes init() throw (typically the vendor module
    // failing to load at all) makes identify() throw too, and this runs inside auth-callback's
    // tap — an escape here would skip the org-SignalR connect and hijack the routing decision.
    try {
      this.identify(user.id, userProperties);
    } catch (error) {
      console.warn('LogRocket failed to identify the user', error);
    }
  }

  trackCustomEvent(eventName: LogRocketEventType | string, properties?: Record<string, any>): void {
    if (!this.enabled) {
      return;
    }

    const eventData = {
      timestamp: new Date().toISOString(),
      ...properties
    };

    try {
      this.track(eventName, eventData);
    } catch (error) {
      console.warn('LogRocket failed to track an event', error);
    }
  }

  // The `logrocket` namespace object is non-writable under the Karma/webpack build, so the
  // vendor calls are wrapped in these thin seams to keep the service spy-able.

  private init(appId: string): void {
    LogRocket.init(appId, {
      console: {
        shouldAggregateConsoleErrors: true,
      },
      network: {
        requestSanitizer: request => {
          if (request.headers['Authorization']) {
            request.headers['Authorization'] = '[REDACTED]';
          }
          return request;
        },
        responseSanitizer: response => {
          return response;
        }
      },
      dom: {
        inputSanitizer: true
      }
    });
  }

  private onSessionUrl(callback: (sessionUrl: string) => void): void {
    LogRocket.getSessionURL(callback);
  }

  private identify(id: string, userProperties: Record<string, any>): void {
    LogRocket.identify(id, userProperties);
  }

  private track(eventName: string, eventData: Record<string, any>): void {
    LogRocket.track(eventName, eventData);
  }

}
